import { NOTIFICATION_FREQUENCY } from '../constants';
import { IUserCalendar } from './calendar.interfaces';

/** User */
export enum ACCOUNT_STATUS {
  FREE = 'FREE',
  ACTIVE = 'ACTIVE',
}

export interface IGoogleAuthToken {
  access_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  id_token: string;
  // optional fields
  expires_in?: number;
  session_state?: {
    extraQueryParams?: {
      authuser?: string;
    };
  };
  first_issued_at?: number;
  expires_at?: number;
  idpId?: string;
  login_hint?: string;
}

// Time block using 24-hour decimal format - unified interface
export interface ITimeBlock {
  start: number; // 24-hour format (e.g., 9 = 9:00 AM, 17.5 = 5:30 PM)
  end: number; // 24-hour format
}

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=Sunday, 1=Monday, ..., 6=Saturday

export interface IWeeklyAvailability {
  [key: number]: ITimeBlock[]; // DayOfWeek -> array of available time blocks
}

export interface IAllowedDurations {
  options: [number, number, number]; // exactly 3 duration options in minutes (use 0 for "flex")
  defaultMinutes?: number; // fallback when 0/"flex" is selected
  flexMinMinutes?: number; // min duration when flex (default 15)
  flexMaxMinutes?: number; // max duration when flex (default 120)
  flexStepMinutes?: number; // step increment when flex (default 15)
}

type ProductivityStyle =
  | 'balanced'
  | 'earlyBird'
  | 'nightOwl'
  | 'poweredByDeadlines';

// Individual availability profile within scheduling preferences
export interface IAvailabilityProfile {
  key: string; // unique identifier (e.g., "general", "client-meetings", "deep-work")
  name: string; // display name (e.g., "General Availability", "Client Meetings")
  description?: string;
  isDefault: boolean; // exactly one profile per user should have this true
  active: boolean; // whether this profile is currently usable

  // Weekly availability for this profile
  weeklyAvailability: IWeeklyAvailability;
  timezone?: string; // IANA timezone, falls back to user.settings.timeZone

  // Meeting constraints for this profile
  minNoticeMinutes: number; // minimum advance notice required
  allowedDurations: IAllowedDurations;
  bufferMinutesBetweenMeetings: number;
  maxConsecutiveMeetings?: number;

  createdAt?: Date;
  updatedAt?: Date;
}

// Scheduling preferences - focused on profiles and user context
export interface IUserSchedulingPreferences {
  /** User's productivity style - helps AI understand preferences */
  productivityStyle?: ProductivityStyle;
  /** Free-text semantic config, e.g. "Prefer mornings, avoid after 4pm" */
  semanticConfig?: string;

  /** Availability profiles system for meetings */
  availabilityProfiles?: IAvailabilityProfile[];

  /** Task scheduling preferences (separate from meeting availability) */
  taskScheduling?: ITaskSchedulingPreferences;
}

/** Task scheduling configuration - similar to availability profiles but for personal tasks */
export interface ITaskSchedulingPreferences {
  /** Semantic config for task scheduling, e.g. "I work best in the morning, prefer 2-hour blocks" */
  semanticConfig?: string;

  /** Daily task time budgets by day of week (indexed 0-6, Sunday=0) */
  dailyTaskBudgets: {
    [dayOfWeek: number]: number; // minutes, e.g., 0: 240 (Sunday: 4 hours), 1: 480 (Monday: 8 hours)
  };

  /** Working hours for task scheduling (can differ from meeting availability) */
  taskWorkingHours: {
    start: number; // 24-hour decimal, e.g., 9 = 9 AM
    end: number; // 24-hour decimal, e.g., 17 = 5 PM
  };

  /** Task distribution preferences */
  distributionStyle?:
    | 'concentrated'
    | 'spread'
    | 'morning-heavy'
    | 'afternoon-heavy';

  /** Minimum task duration to schedule (minutes) */
  minTaskDuration?: number; // default 15

  /** Buffer time between tasks (minutes) */
  bufferBetweenTasks?: number; // default 5

  /** Whether to respect existing calendar events */
  respectCalendarEvents?: boolean; // default true

  createdAt?: Date;
  updatedAt?: Date;
}

// Public Profile interface
export interface IPublicProfile {
  userId: string; // object ID for mongoDB to join to user
  canonicalId: string; // permanent user identifier (UUID)
  isPublic: boolean; // Determines if the profile is publicly visible
  name?: string; // User's full name
  username?: string; // Unique username for the public profile
  bio?: string; // User bio, limited to 500 characters
  profilePictureUrl?: string; // URL to user's profile picture
  socialLinks?: {
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    youtube?: string;
    website?: string;
  }; // Object to store user's social media links
}

/* Sync Operation Interface - reusable for any sync type */
export interface ISyncOperation {
  lastSyncDate?: Date;
  lastSyncAttempt?: Date;
  syncFrequencyMinutes?: number; // Default 60 minutes
  autoSyncEnabled?: boolean; // Default true
  consecutiveFailures?: number; // Default 0
  lastError?: string;
  lastSuccessfulSync?: Date;
  totalSyncs?: number; // Default 0
  createdAt?: Date;
  updatedAt?: Date;
}

/* Sync Tracking Interface - modular and extensible */
export interface ISyncTracking {
  calendar?: ISyncOperation;
  tasks?: ISyncOperation;
  reminders?: ISyncOperation;
  integrations?: ISyncOperation;
  analytics?: ISyncOperation;
  // Future sync operations can be added here easily
}

/* User Interface */
/** Keysha AI Configuration - Adaptive Personality & Learning */
export interface IKeyshaConfig {
  personality: string; // Natural language description (e.g., "friendly, warm, concise, occasional emojis")
  enableMemoryStorage: boolean; // Opt-in for vector knowledge base
  memoryRetentionDays: number; // Privacy: how long to keep memories
  lastUpdated: Date;
  detectedFromMessages: number; // Confidence: more messages = better personality detection

  // Engagement tracking (performance: stored, not calculated!)
  engagementLevel?: 'fast' | 'moderate' | 'slow'; // How quickly user responds
  avgResponseTimeMinutes?: number; // Average response time
  lastEngagementUpdate?: Date; // When engagement was last calculated
}

export interface IUser {
  _id?: string; // Optional, as it is generated by MongoDB
  canonicalId: string; // Permanent UUID identifier (public, never changes)
  firstName?: string; // User's first name
  lastName?: string; // User's last name
  mobile?: string; // User's mobile number
  email: string; // User's email address
  photoUrl?: string; // URL to the user's profile picture
  bio?: string; // User's bio
  firebaseUid?: string; // Firebase user ID
  auth0Uid?: string[]; // Auth0 user IDs
  provider?: string; // Authentication provider
  totalTaskPoints: number;
  referralCode?: string; // User's referral code
  publicProfileLink?: string; // Public profile URL
  dailyMessageData: {
    count: number; // Number of messages sent today
    monthCount: number; // Number of messages sent this month
    lastMessageDate: Date; // Date of the last message sent
    rateLimitNotified?: boolean; // Whether the user has been notified about rate limits
  };
  settings: {
    isOnboarded: boolean; // Whether the user has completed onboarding
    dailyMessageLimit: number; // Daily message limit
    monthlyMessageLimit: number; // Monthly message limit
    timeZone?: {
      name?: string; // Time zone name
      offset?: number; // Time zone offset
    };
    accountStatus: ACCOUNT_STATUS; // User's account status
    sendReminders: boolean; // Whether to send reminders
    /** @deprecated - Will be removed after migration to new calendar system */
    // googleCalendar?: {
    //   email?: string; // Google Calendar email
    //   googleUserId?: string; // Google User ID
    //   googleAuthResponse?: IGoogleAuthToken;
    // };
    calendars: IUserCalendar[]; // Array of connected calendars
    stripe?: {
      customerId?: string; // Stripe customer ID
      subscriptionId?: string; // Stripe subscription ID
    };
    referralCode?: string; // Referral code
    referrer?: string; // Referrer ID
    keyshaConfig?: IKeyshaConfig; // AI personality & learning configuration
    chatData?: {
      welcomeMessageSent?: boolean; // Whether the welcome message has been sent (only flag needed)
    };
    verifyMobileData?: {
      isMobileVerified?: boolean; // Whether the mobile number is verified
      verifyMobileCode?: string; // Verification code
      codeExpirationDate?: Date; // Expiration date of the verification code
      unverifiedMobile?: string; // Unverified mobile number
      otpAttempts?: number; // Number of failed OTP attempts
      lastAttemptTime?: Date; // Timestamp of last OTP attempt (for rate limiting)
    };
    preferences?: {
      userSchedulingPreferences?: IUserSchedulingPreferences;
      notifications?: {
        channels: {
          email: boolean; // Whether to send email notifications
          sms: boolean; // Whether to send SMS notifications
          push: boolean; // Whether to send push notifications
          reminders: boolean; // Whether to send reminder notifications
        };
        frequency: NOTIFICATION_FREQUENCY; // Notification frequency
        reminderFrequency: NOTIFICATION_FREQUENCY; // Reminder frequency
        subscriptions: {
          messages: boolean; // Subscription to message notifications
          reminders: boolean; // Subscription to reminder notifications
          promotions: boolean; // Subscription to promotion notifications
          systemUpdates: boolean; // Subscription to system update notifications
        };
        lastNotificationSent: {
          messages: Date | null; // Last message notification sent date
          reminders: Date | null; // Last reminder notification sent date
          promotions: Date | null; // Last promotion notification sent date
          systemUpdates: Date | null; // Last system update notification sent date
        };
      };
    };
    // Sync tracking for auto-sync operations based on user activity
    syncTracking?: ISyncTracking;
  };
}
